import { useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { getMe } from '@/features/user/userSlice';
import RestaurantShell from '@/components/layout/RestaurantShell';
import MenuPage from '@/features/menu/MenuPage';
import NotFoundPage from '@/pages/NotFoundPage';
import CheckoutPage from '@/features/checkout/CheckoutPage';
import ConfirmationPage from '@/features/confirmation/ConfirmationPage';
import ReservationPage from '@/features/reservation/ReservationPage';
import ReservationConfirmationPage from '@/features/reservation/ReservationConfirmationPage';
import { RestaurantRouteGuard } from '@/components/RestaurantRouteGuard';
import { AccountGuard } from '@/features/user/AccountGuard';
import AccountSubLayout from '@/features/user/pages/AccountSubLayout';
import AccountHomePage from '@/features/user/pages/AccountHomePage';
import ProfilePage from '@/features/user/pages/ProfilePage';
import OrdersPage from '@/features/user/pages/OrdersPage';
import OrderDetailPage from '@/features/user/pages/OrderDetailPage';
import AddressesPage from '@/features/user/pages/AddressesPage';
import LoyaltyPage from '@/features/loyalty/pages/LoyaltyPage';
import ResetPasswordShell from '@/features/user/pages/ResetPasswordShell';
import SurveyShell from '@/features/survey/SurveyShell';
import PaymentReturnPage from '@/pages/PaymentReturnPage';
import TableRoute from '@/features/tableSession/TableRoute';
import TablePaymentReturn from '@/features/tableSession/TablePaymentReturn';
import WaiterShell from '@/features/waiter/WaiterShell';
import WaiterPendingOrdersHost from '@/features/waiter/WaiterPendingOrdersHost';
import StaleBuildBanner from '@/features/waiter/StaleBuildBanner';

// SC-684: le veilleur « commande QR en attente » se monte au-dessus de TOUTES
// les pages du restaurant — la grille des tables du serveur comme l'écran de
// prise de commande — pour que le popup arrive quelle que soit la page ouverte.
// Il ne rend rien tant que l'appareil n'est pas déverrouillé en mode serveur.
function RestaurantRoutes() {
  return (
    <>
      <WaiterPendingOrdersHost />
      {/* SC-763: meme porte que le veilleur ci-dessus — monte partout, visible
          seulement sur la surface du personnel. Le carnet du serveur reste
          ouvert des heures et ne va jamais rechercher un nouveau paquet tout
          seul ; ce bandeau est ce qui le lui dit. */}
      <StaleBuildBanner />
      <Outlet />
    </>
  );
}

const router = createBrowserRouter([
  { path: '/', element: <NotFoundPage /> },
  { path: '/order/:uuid/payment-return', element: <PaymentReturnPage /> },
  {
    path: '/:restaurantUuid',
    element: <RestaurantRouteGuard />,
    children: [
      {
        element: <RestaurantRoutes />,
        children: [
          // Waiter handheld mode mounts OUTSIDE RestaurantShell: the shell gates
          // on identity.onlineOrderingFeatureEnabled and activates dine-in state
          // only on /table/ routes, neither of which should apply here.
          // WaiterShell fetches its own restaurant identity for theming instead.
          // `/*` (not a bare 'serveur') — F2/D1: the ticket screen
          // (`serveur/ticket/:sessionUuid`) is a route WaiterShell renders
          // itself, nested under the PIN gate / idle lock / ThemeProvider it
          // already owns, rather than a sibling that would bypass all three.
          { path: 'serveur/*', element: <WaiterShell /> },
          // DF-0297/DF-0298 — la page « definir mon mot de passe » est soeur de
          // `serveur/*`, pas fille de `RestaurantShell` : celui-ci la fermait
          // derriere les portes commerciales du restaurant (module eteint =>
          // 404 sur une enseigne dont le jeton etait pourtant valide) et posait
          // la fenetre d'accueil par-dessus le formulaire. La coque recupere
          // son identite elle-meme, pour le theme seulement.
          { path: 'reset-password', element: <ResetPasswordShell /> },
          // SC-1752 — questionnaire client : meme raison que `reset-password`, il doit
          // rester ouvert quand la commande en ligne est eteinte et sans annonce par-dessus.
          { path: 'avis/:surveyUuid', element: <SurveyShell /> },
          {
            element: <RestaurantShell />,
            children: [
              { index: true, element: <MenuPage /> },
              { path: 'checkout', element: <CheckoutPage /> },
              { path: 'confirmation/:orderId', element: <ConfirmationPage /> },
              { path: 'reservation', element: <ReservationPage /> },
              {
                path: 'reservation/confirmation/:reservationId',
                element: <ReservationConfirmationPage />,
              },
              // Dine-in (scan-to-order) lives under the table token so its URL stays
              // distinct from the online-ordering paths above.
              { path: 'table/:tableToken', element: <TableRoute /> },
              { path: 'table/:tableToken/checkout', element: <CheckoutPage /> },
              { path: 'table/:tableToken/confirmation/:orderId', element: <ConfirmationPage /> },
              // Retour de la page hebergee Stancer apres un paiement autonome de
              // la table. L'URL est composee serveur (?tsp=<uuid du paiement>).
              { path: 'table/:tableToken/payment-return', element: <TablePaymentReturn /> },
              { path: 'cart', element: <Navigate to=".." replace /> },
              {
                path: 'account',
                element: (
                  <AccountGuard>
                    <Outlet />
                  </AccountGuard>
                ),
                children: [
                  { index: true, element: <AccountHomePage /> },
                  {
                    path: 'profile',
                    element: (
                      <AccountSubLayout titleKey="account_sub.profile">
                        <ProfilePage />
                      </AccountSubLayout>
                    ),
                  },
                  {
                    path: 'orders',
                    element: (
                      <AccountSubLayout titleKey="account_sub.orders">
                        <OrdersPage />
                      </AccountSubLayout>
                    ),
                  },
                  {
                    path: 'orders/:orderId',
                    element: (
                      <AccountSubLayout titleKey="account_sub.detail">
                        <OrderDetailPage />
                      </AccountSubLayout>
                    ),
                  },
                  {
                    path: 'addresses',
                    element: (
                      <AccountSubLayout titleKey="account_sub.addresses">
                        <AddressesPage />
                      </AccountSubLayout>
                    ),
                  },
                  {
                    path: 'loyalty',
                    element: (
                      // .loyalty-scope: the zh font fix must reach the page title too.
                      <div className="loyalty-scope">
                        <AccountSubLayout titleKey="loyalty.pageTitle">
                          <LoyaltyPage />
                        </AccountSubLayout>
                      </div>
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
]);

function BootSessionRehydrate() {
  const dispatch = useAppDispatch();
  const token = useAppSelector((s) => s.user.token);
  useEffect(() => {
    if (token) {
      dispatch(getMe());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

export default function App() {
  return (
    <>
      <BootSessionRehydrate />
      <RouterProvider router={router} />
    </>
  );
}
